import { useReactiveVar } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import {
    isContentTeam,
    isOmWorkspaceUser,
} from 'api/graphQL/reactiveVars/workspaceContent'
import React from 'react'
import { useParams } from 'react-router-dom'

/**
 * Custom hook to determine if the user is currently viewing the OM (Office Master) Workspace.
 *
 * The OM Workspace has special behavior:
 * - Allows editing of inactive content
 * - Hides track changes styling
 * - Shows visibility toggles for content activation/deactivation
 *
 * @returns {Object} OM workspace state
 * @returns {boolean} isCurrentlyOnOMWorkspace - True if user is an OM user AND current project is the best practice project
 * @returns {boolean} isOMUser - True if the current user has OM workspace access
 *
 * @example
 * ```tsx
 * const { isCurrentlyOnOMWorkspace, isOMUser } = useOMWorkspace()
 *
 *
 * // Allow editing inactive content in OM workspace
 * const isEditMode = shouldEdit && (isCurrentlyOnOMWorkspace ? true : !inactive)
 * ```
 */
export const useOMWorkspace = () => {
    const isOMUser = useReactiveVar(isOmWorkspaceUser)
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const { currentUserActiveFirm } = React.useContext(AppBarContext)
    const { projectId } = useParams<{
        projectId: string
    }>()
    /**
     * Determines if the user is currently viewing the OM Workspace.
     * True when user has OM access and current project is the best practice project.
     */
    const isCurrentlyOnOMWorkspace = Boolean(
        isContentTeamUser ||
            (isOMUser &&
                projectId != null &&
                currentUserActiveFirm?.bestPracticeProjectId === projectId)
    )

    return {
        /**
         * True if user has OM access and is on the best practice project.
         * Use this for OM-specific behavior like hiding track changes or allowing inactive content editing.
         */
        isCurrentlyOnOMWorkspace,

        /**
         * True if the current user has OM workspace access.
         */
        isOMUser,
    }
}
